import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Building,
  RefreshCw,
  Trash2,
  Pencil,
  ImageIcon,
  History,
  FileText,
  Loader2,
  Search,
  GitMerge,
  Plus,
  Download,
} from "lucide-react";
import { TIPI_STRUTTURA } from "../../config/tipiStruttura";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { AdminPageHeader } from "./components/AdminUi";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CATALOGO = {
  name: "",
  tipoStruttura: "RSA",
  comune: "",
  provincia: "",
  via: "",
  numeroCivico: "",
  cap: "",
  telefonoStruttura: "",
  emailStruttura: "",
  sitoWeb: "",
  facebook: "",
  postiTotali: "",
  postiDiurni: "",
  tariffaSingolaWc: "",
  tariffaSingolaNoWc: "",
  tariffaDoppiaWc: "",
  tariffaDoppiaNoWc: "",
  nomeReferente: "",
  telefonoReferente: "",
  presenzaMedico: "",
  presenzaInfermiere: "",
  attivitaOccupazionali: "",
  giardinoSpaziEsterni: "",
  descrizione: "",
  visibile_cdrpt: true,
};

function numOrNull(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

function apiError(json, fallback) {
  const detail = json?.detail;
  if (typeof detail === "string" && detail) return detail;
  return fallback;
}

function field(label, value) {
  const display = value === 0 || value === false ? String(value) : value || "—";
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 py-1 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 break-words">{display}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-700 mb-2 border-b pb-1">{title}</h3>
      <div className="space-y-0">{children}</div>
    </div>
  );
}

export default function StruttureAdminModule({ canMerge = true, canDelete = true }) {
  const token = localStorage.getItem("super_admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroAttivo, setFiltroAttivo] = useState("true");
  const [q, setQ] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [attivita, setAttivita] = useState([]);
  const [showAttivita, setShowAttivita] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [merging, setMerging] = useState(false);
  const [mergePick, setMergePick] = useState({ keepId: null, mergeId: null });
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CATALOGO);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ attivo: filtroAttivo });
      if (q.trim()) params.set("q", q.trim());
      const res = await fetch(`${API}/api/admin/strutture?${params}`, { headers });
      if (!res.ok) throw new Error("Errore caricamento strutture");
      const json = await res.json();
      setItems(json.strutture || []);
    } catch (e) {
      toast.error(e.message || "Errore");
    } finally {
      setLoading(false);
    }
  }, [filtroAttivo, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ attivo: filtroAttivo });
      if (q.trim()) params.set("q", q.trim());
      const res = await fetch(`${API}/api/admin/strutture/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(apiError(json, "Esportazione non riuscita"));
      }
      const blob = await res.blob();
      const name = (res.headers.get("content-disposition") || "").match(/filename="?([^";]+)"?/i)?.[1]
        || "strutture.csv";
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      toast.success("Esportazione CSV scaricata");
    } catch (e) {
      toast.error(e.message || "Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const fetchDetail = async (id) => {
    setDetailLoading(true);
    setDetail(null);
    setEditing(false);
    try {
      const res = await fetch(`${API}/api/admin/strutture/${id}`, { headers });
      if (!res.ok) throw new Error("Dettaglio non disponibile");
      setDetail(await res.json());
    } catch (e) {
      setDetail({ error: e.message });
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetail = (id) => {
    setDetailId(id);
    setShowAttivita(false);
    fetchDetail(id);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
    setEditing(false);
    setShowAttivita(false);
  };

  const startEdit = () => {
    if (!detail || detail.error) return;
    setForm({
      name: detail.name || "",
      tipoStruttura: detail.tipoStruttura || "",
      comune: detail.comune || "",
      provincia: detail.provincia || "",
      via: detail.via || "",
      numeroCivico: detail.numeroCivico || "",
      cap: detail.cap || "",
      telefonoStruttura: detail.telefonoStruttura || "",
      emailStruttura: detail.emailStruttura || detail.email || "",
      sitoWeb: detail.sitoWeb || "",
      facebook: detail.facebook || "",
      postiTotali: detail.postiTotali ?? "",
      postiDisponibili: detail.postiDisponibili ?? "",
      tariffaSingolaWc: detail.tariffaSingolaWc ?? "",
      tariffaSingolaNoWc: detail.tariffaSingolaNoWc ?? "",
      tariffaDoppiaWc: detail.tariffaDoppiaWc ?? "",
      tariffaDoppiaNoWc: detail.tariffaDoppiaNoWc ?? "",
      camereSingolaWc: detail.camereSingolaWc ?? "",
      camereSingolaNoWc: detail.camereSingolaNoWc ?? "",
      camereDoppiaWc: detail.camereDoppiaWc ?? "",
      camereDoppiaNoWc: detail.camereDoppiaNoWc ?? "",
      nomeReferente: detail.nomeReferente || "",
      telefonoReferente: detail.telefonoReferente || "",
      emailReferente: detail.emailReferente || "",
      nomeReferente2: detail.nomeReferente2 || "",
      telefonoReferente2: detail.telefonoReferente2 || "",
      emailReferente2: detail.emailReferente2 || "",
      descrizione: detail.descrizione || "",
      noteCallCenter: detail.noteCallCenter || "",
      noteAppuntamenti: detail.noteAppuntamenti || "",
      noteCommerciali: detail.noteCommerciali || "",
      presenzaMedico: detail.presenzaMedico || "",
      presenzaInfermiere: detail.presenzaInfermiere || "",
      attivitaOccupazionali: detail.attivitaOccupazionali || "",
      giardinoSpaziEsterni: detail.giardinoSpaziEsterni || "",
      ospitiAutosufficienti: !!detail.ospitiAutosufficienti,
      ospitiParzialmenteAutosufficienti: !!detail.ospitiParzialmenteAutosufficienti,
      ospitiAllettati: !!detail.ospitiAllettati,
      ospitiAlzheimer: !!detail.ospitiAlzheimer,
      ospitiSediaRotelle: !!detail.ospitiSediaRotelle,
      cucinaInterna: !!detail.cucinaInterna,
      attivo: !!detail.attivo,
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!detailId) return;
    setSaving(true);
    try {
      const payload = { ...form };
      ["postiTotali", "postiDisponibili", "tariffaSingolaWc", "tariffaSingolaNoWc", "tariffaDoppiaWc", "tariffaDoppiaNoWc",
        "camereSingolaWc", "camereSingolaNoWc", "camereDoppiaWc", "camereDoppiaNoWc"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) return;
        const n = Number(payload[k]);
        if (!Number.isNaN(n)) payload[k] = n;
      });
      const res = await fetch(`${API}/api/admin/strutture/${detailId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Salvataggio fallito");
      if (json.warning) toast.warning(json.warning);
      else toast.success("Struttura aggiornata");
      setEditing(false);
      await fetchDetail(detailId);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleVisibileCdrpt = async (id, next) => {
    try {
      const res = await fetch(`${API}/api/admin/strutture/${id}/visibilita-cdrpt`, {
        method: "POST",
        headers,
        body: JSON.stringify({ visibile: next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiError(json, "Errore pubblicazione"));
      toast.success(next ? "Struttura visibile sull'app CasaDiRiposoPerTe" : "Struttura nascosta sull'app CasaDiRiposoPerTe");
      load();
      if (detailId === id) fetchDetail(id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const createStruttura = async () => {
    if (!createForm.name.trim()) {
      toast.error("Indica il nome della struttura");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...createForm,
        name: createForm.name.trim(),
        postiTotali: numOrNull(createForm.postiTotali),
        postiDiurni: numOrNull(createForm.postiDiurni),
        tariffaSingolaWc: numOrNull(createForm.tariffaSingolaWc),
        tariffaSingolaNoWc: numOrNull(createForm.tariffaSingolaNoWc),
        tariffaDoppiaWc: numOrNull(createForm.tariffaDoppiaWc),
        tariffaDoppiaNoWc: numOrNull(createForm.tariffaDoppiaNoWc),
      };
      const res = await fetch(`${API}/api/admin/strutture`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiError(json, "Inserimento non riuscito"));
      if (json.warning) toast.warning(json.warning);
      else if (json.visibile_cdrpt) toast.success("Struttura inserita e visibile sull'app CasaDiRiposoPerTe");
      else toast.success("Struttura inserita. Non è visibile sull'app: puoi attivarla dalla scheda.");
      setCreateOpen(false);
      setCreateForm(EMPTY_CATALOGO);
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  const toggleAttivo = async (id, next) => {
    try {
      const res = await fetch(`${API}/api/admin/strutture/${id}/attivo`, {
        method: "POST",
        headers,
        body: JSON.stringify({ attivo: next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Errore");
      toast.success(next ? "Struttura attivata" : "Struttura disattivata");
      load();
      if (detailId === id) fetchDetail(id);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const loadDuplicates = async () => {
    try {
      const res = await fetch(`${API}/api/admin/strutture/duplicates`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Errore");
      setDuplicates(json.data || []);
      setDupOpen(true);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const runMerge = async (keepId, mergeId) => {
    if (!keepId || !mergeId) return;
    if (
      !window.confirm(
        "Unisci le due schede? Verrà tenuta quella con account Affidya (utenti/org) e assorbiti i dati CRM Zoho dall'altra, che verrà eliminata."
      )
    ) {
      return;
    }
    setMerging(true);
    try {
      const res = await fetch(`${API}/api/admin/strutture/merge`, {
        method: "POST",
        headers,
        body: JSON.stringify({ keep_id: keepId, merge_id: mergeId, auto_pick: true }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Merge fallito");
      toast.success("Schede unite correttamente");
      setDupOpen(false);
      setMergePick({ keepId: null, mergeId: null });
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setMerging(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare "${name || id}"?`)) return;
    setDeletingId(id);
    try {
      const res = await fetch(`${API}/api/admin/strutture/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.detail || "Impossibile eliminare");
      toast.success(json.message || "Eliminata");
      if (detailId === id) closeDetail();
      load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const loadAttivita = async () => {
    if (!detailId) return;
    setShowAttivita(true);
    try {
      const res = await fetch(`${API}/api/admin/strutture/${detailId}/attivita`, { headers });
      const json = await res.json();
      setAttivita(json.data || []);
    } catch {
      setAttivita([]);
    }
  };

  const uploadImage = async (file) => {
    if (!file || !detailId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/strutture/${detailId}/immagini`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(apiError(json, "Upload fallito"));
      if (json.warning) toast.warning(json.warning);
      else toast.success("Immagine caricata");
      fetchDetail(detailId);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const openSchedaPdf = async () => {
    if (!detailId) return;
    try {
      const res = await fetch(`${API}/api/admin/strutture/${detailId}/scheda-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const detail = json.detail;
        throw new Error(typeof detail === "string" ? detail : "Impossibile generare il PDF");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e.message || "Errore generazione PDF");
    }
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setCreate = (k, v) => setCreateForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Building}
        title="Strutture"
        description="Anagrafica comune. Puoi inserire una struttura senza creare prima un utente: con l'interruttore resta visibile sull'app di prenotazione di CasaDiRiposoPerTe. Di default l'elenco mostra solo le schede attive."
        actions={
        <>
          <Button
            size="sm"
            onClick={() => {
              setCreateForm(EMPTY_CATALOGO);
              setCreateOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuova struttura
          </Button>
          {canMerge && (
            <Button onClick={loadDuplicates} variant="outline" size="sm">
              <GitMerge className="h-4 w-4 mr-2" />
              Unisci duplicati
            </Button>
          )}
          <Button
            onClick={exportCsv}
            variant="outline"
            size="sm"
            disabled={exporting}
            title="Scarica le strutture del filtro corrente"
          >
            <Download className={`h-4 w-4 mr-2 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Esportazione…" : "Esporta CSV"}
          </Button>
          <Button onClick={load} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </>
        }
      />
      {canMerge && (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2">
        Se vedi due righe per la stessa struttura (una con utenti/organizzazione e una senza),
        usa <strong>Unisci duplicati</strong>: tiene l&apos;account Affidya e importa i dati Zoho
        (rette, note, attivo…) dall&apos;altra scheda.
      </p>
      )}

      <div className="flex flex-wrap gap-3 items-end">
        <div className="w-44">
          <Label className="text-xs">Filtro</Label>
          <Select value={filtroAttivo} onValueChange={setFiltroAttivo}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">Attive</SelectItem>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="false">Non attive</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Ricerca</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              className="pl-8"
              placeholder="Nome, comune…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={load}>
          Cerca
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="admin-table-sticky-actions w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Struttura</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Stato</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Organizzazione</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Posti</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Utenti</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase w-28">Azioni</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
                      Caricamento…
                    </td>
                  </tr>
                ) : !items.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                      Nessuna struttura trovata
                    </td>
                  </tr>
                ) : (
                  items.map((s) => (
                    <tr
                      key={s.id}
                      onClick={() => openDetail(s.id)}
                      className="cursor-pointer hover:bg-slate-50"
                    >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 flex items-center gap-2 flex-wrap">
                        {s.name}
                        {s.catalogo_cdrpt && (
                          <Badge variant="outline" className="text-violet-700 border-violet-300 bg-violet-50">
                            Senza account
                          </Badge>
                        )}
                        {s.is_zoho_orphan && (
                          <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50">
                            Solo Zoho
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        {[s.tipoStruttura, s.comune, s.provincia].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!!s.attivo}
                            onCheckedChange={(v) => toggleAttivo(s.id, v)}
                          />
                          <Badge variant={s.attivo ? "default" : "secondary"}>
                            {s.attivo ? "Attiva" : "Non attiva"}
                          </Badge>
                          {s.premium && <Badge variant="outline">Premium</Badge>}
                        </div>
                        {s.catalogo_cdrpt && (
                          <div className="mt-2 flex items-center gap-2">
                            <Switch
                              checked={!!s.visibile_cdrpt}
                              onCheckedChange={(v) => toggleVisibileCdrpt(s.id, v)}
                              aria-label="Visibilità su CasaDiRiposoPerTe"
                            />
                            <span className="text-xs text-slate-500">Su CasaDiRiposoPerTe</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.organizationName || "—"}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {s.postiDisponibili != null ? `${s.postiDisponibili}/` : ""}
                        {s.postiTotali ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">{s.stats?.users ?? 0}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {canMerge && s.is_zoho_orphan && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-blue-600"
                              title="Unisci a un account Affidya omonimo"
                              onClick={() => {
                                const twin = items.find(
                                  (o) =>
                                    o.id !== s.id &&
                                    !o.is_zoho_orphan &&
                                    (o.name || "").toLowerCase() === (s.name || "").toLowerCase()
                                );
                                if (twin) runMerge(twin.id, s.id);
                                else {
                                  toast.message("Apri «Unisci duplicati» per scegliere la coppia");
                                  loadDuplicates();
                                }
                              }}
                            >
                              <GitMerge className="h-4 w-4" />
                            </Button>
                          )}
                          {canDelete && ((s.stats?.users ?? 0) === 0 || s.is_zoho_orphan) ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600"
                              disabled={deletingId === s.id}
                              onClick={() => handleDelete(s.id, s.name)}
                            >
                              {deletingId === s.id ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-600" />
              Nuova struttura
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            La struttura entra in anagrafica senza un account utente. Se la lasci visibile,
            compare nell&apos;app di prenotazione di CasaDiRiposoPerTe.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Nome *</Label>
              <Input value={createForm.name} onChange={(e) => setCreate("name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={createForm.tipoStruttura} onValueChange={(v) => setCreate("tipoStruttura", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_STRUTTURA.map((tipo) => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {[
              ["comune", "Comune"],
              ["provincia", "Provincia"],
              ["via", "Via"],
              ["numeroCivico", "Civico"],
              ["cap", "CAP"],
              ["telefonoStruttura", "Telefono"],
              ["emailStruttura", "Email"],
              ["sitoWeb", "Sito"],
              ["facebook", "Facebook"],
              ["postiTotali", "Posti totali"],
              ["postiDiurni", "Posti diurni"],
              ["tariffaSingolaWc", "Retta singola WC"],
              ["tariffaSingolaNoWc", "Retta singola no WC"],
              ["tariffaDoppiaWc", "Retta doppia WC"],
              ["tariffaDoppiaNoWc", "Retta doppia no WC"],
              ["nomeReferente", "Referente"],
              ["telefonoReferente", "Tel. referente"],
            ].map(([k, lab]) => (
              <div key={k}>
                <Label className="text-xs">{lab}</Label>
                <Input value={createForm[k] ?? ""} onChange={(e) => setCreate(k, e.target.value)} />
              </div>
            ))}
            <div className="md:col-span-2">
              <Label className="text-xs">Descrizione</Label>
              <Textarea
                value={createForm.descrizione}
                onChange={(e) => setCreate("descrizione", e.target.value)}
                rows={3}
              />
            </div>
            <div className="md:col-span-2 flex flex-wrap gap-4 text-sm">
              {[
                ["presenzaMedico", "Medico"],
                ["presenzaInfermiere", "Infermieri"],
                ["attivitaOccupazionali", "Attività occupazionali"],
                ["giardinoSpaziEsterni", "Giardino"],
              ].map(([k, lab]) => (
                <label key={k} className="flex items-center gap-2">
                  <Switch
                    checked={createForm[k] === "Sì"}
                    onCheckedChange={(v) => setCreate(k, v ? "Sì" : "")}
                  />
                  {lab}
                </label>
              ))}
            </div>
            <div className="md:col-span-2 flex items-center justify-between gap-3 rounded border border-slate-200 px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-800">Visibile sull&apos;app CasaDiRiposoPerTe</p>
                <p className="text-xs text-slate-500">Le famiglie la trovano nella ricerca e possono prenotare una visita.</p>
              </div>
              <Switch
                checked={!!createForm.visibile_cdrpt}
                onCheckedChange={(v) => setCreate("visibile_cdrpt", v)}
                aria-label="Visibile sull'app CasaDiRiposoPerTe"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={creating}>
              Annulla
            </Button>
            <Button onClick={createStruttura} disabled={creating}>
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Inserisci struttura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitMerge className="h-5 w-5 text-blue-600" />
              Unisci schede duplicate
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Per ogni coppia: viene tenuta la sede con <strong>account Affidya</strong> (organizzazione/utenti)
            e vengono importati i dati CRM dalla scheda <strong>Solo Zoho</strong>, che viene eliminata.
          </p>
          {!duplicates.length ? (
            <p className="text-sm text-slate-500 py-6 text-center">Nessun duplicato suggerito</p>
          ) : (
            <ul className="space-y-3">
              {duplicates.map((d, idx) => (
                <li key={d.key + idx} className="border rounded-lg p-3 bg-slate-50">
                  <div className="font-medium text-slate-900">
                    {d.nome}
                    {d.comune ? ` · ${d.comune}` : ""}
                  </div>
                  <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                    {(d.candidates || []).map((c) => (
                      <div key={c.id}>
                        {c.orphan || c.id === d.merge_id ? "Solo Zoho" : "Account Affidya"}: {c.name}{" "}
                        <span className="text-slate-400">({c.id.slice(-6)})</span>
                      </div>
                    ))}
                  </div>
                  {d.keep_id && d.merge_id && (
                    <Button
                      size="sm"
                      className="mt-2"
                      disabled={merging}
                      onClick={() => runMerge(d.keep_id, d.merge_id)}
                    >
                      {merging ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <GitMerge className="h-4 w-4 mr-1" />}
                      Unisci questa coppia
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDupOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detailId} onOpenChange={(open) => !open && closeDetail()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="h-5 w-5 text-green-500" />
              Dettaglio struttura
            </DialogTitle>
          </DialogHeader>
          {detailLoading && (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          )}
          {!detailLoading && detail?.error && (
            <p className="text-red-600 py-4">{detail.error}</p>
          )}
          {!detailLoading && detail && !detail.error && (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={startEdit}>
                  <Pencil className="h-4 w-4 mr-1" /> Modifica
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => document.getElementById("struttura-img-upload")?.click()}
                >
                  <ImageIcon className="h-4 w-4 mr-1" />
                  {uploading ? "Caricamento…" : "Carica immagini"}
                </Button>
                <input
                  id="struttura-img-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    uploadImage(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
                <Button size="sm" variant="outline" onClick={loadAttivita}>
                  <History className="h-4 w-4 mr-1" /> Storico attività
                </Button>
                <Button size="sm" variant="outline" onClick={openSchedaPdf}>
                  <FileText className="h-4 w-4 mr-1" /> Genera scheda PDF
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-slate-500">Attiva</span>
                  <Switch
                    checked={!!detail.attivo}
                    onCheckedChange={(v) => toggleAttivo(detailId, v)}
                  />
                </div>
                {detail.origine === "catalogo_cdrpt" && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Su CasaDiRiposoPerTe</span>
                    <Switch
                      checked={!!detail.visibile_cdrpt}
                      onCheckedChange={(v) => toggleVisibileCdrpt(detailId, v)}
                      aria-label="Visibilità su CasaDiRiposoPerTe"
                    />
                  </div>
                )}
              </div>

              {editing ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    ["name", "Nome"],
                    ["tipoStruttura", "Tipo"],
                    ["comune", "Comune"],
                    ["provincia", "Provincia"],
                    ["via", "Via"],
                    ["numeroCivico", "Civico"],
                    ["cap", "CAP"],
                    ["telefonoStruttura", "Telefono"],
                    ["emailStruttura", "Email"],
                    ["sitoWeb", "Sito"],
                    ["facebook", "Facebook"],
                    ["postiTotali", "Posti totali"],
                    ["postiDisponibili", "Posti disponibili"],
                    ["tariffaSingolaWc", "Retta singola WC"],
                    ["tariffaSingolaNoWc", "Retta singola no WC"],
                    ["tariffaDoppiaWc", "Retta doppia WC"],
                    ["tariffaDoppiaNoWc", "Retta doppia no WC"],
                    ["nomeReferente", "Referente"],
                    ["telefonoReferente", "Tel. referente"],
                    ["emailReferente", "Email referente"],
                  ].map(([k, lab]) => (
                    <div key={k}>
                      <Label className="text-xs">{lab}</Label>
                      <Input value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <Label className="text-xs">Descrizione</Label>
                    <Textarea value={form.descrizione || ""} onChange={(e) => set("descrizione", e.target.value)} rows={3} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Note call center</Label>
                    <Textarea value={form.noteCallCenter || ""} onChange={(e) => set("noteCallCenter", e.target.value)} rows={2} />
                  </div>
                  <div className="md:col-span-2 flex flex-wrap gap-4 text-sm">
                    {[
                      ["ospitiAutosufficienti", "Autosufficienti"],
                      ["ospitiParzialmenteAutosufficienti", "Parz. autosufficienti"],
                      ["ospitiAllettati", "Allettati"],
                      ["ospitiAlzheimer", "Alzheimer"],
                      ["ospitiSediaRotelle", "Sedia a rotelle"],
                      ["cucinaInterna", "Cucina interna"],
                    ].map(([k, lab]) => (
                      <label key={k} className="flex items-center gap-2">
                        <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} />
                        {lab}
                      </label>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <Section title="Anagrafica">
                    {field("Nome", detail.name)}
                    {field("Tipo", detail.tipoStruttura)}
                    {field("Comune", detail.comune)}
                    {field("Provincia", detail.provincia)}
                    {field("Indirizzo", [detail.via, detail.numeroCivico, detail.cap].filter(Boolean).join(" "))}
                    {field("Attiva", detail.attivo ? "Sì" : "No")}
                    {detail.origine === "catalogo_cdrpt" && field("Su CasaDiRiposoPerTe", detail.visibile_cdrpt ? "Visibile" : "Nascosta")}
                    {detail.origine === "catalogo_cdrpt" && field("Account utente", "Nessuno")}
                    {field("Premium", detail.premium ? "Sì" : "No")}
                    {field("Zoho ID", detail.zohoRecordId)}
                  </Section>
                  <Section title="Contatti e referenti">
                    {field("Telefono", detail.telefonoStruttura)}
                    {field("Email", detail.emailStruttura || detail.email)}
                    {field("Sito", detail.sitoWeb)}
                    {field("Facebook", detail.facebook)}
                    {field("Referente", detail.nomeReferente)}
                    {field("Tel. referente", detail.telefonoReferente)}
                    {field("Email referente", detail.emailReferente)}
                    {field("Referente 2", detail.nomeReferente2)}
                    {field("Tel. referente 2", detail.telefonoReferente2)}
                    {field("Email referente 2", detail.emailReferente2)}
                  </Section>
                  <Section title="Camere e rette">
                    {field("Singole WC", detail.camereSingolaWc)}
                    {field("Singole no WC", detail.camereSingolaNoWc)}
                    {field("Doppie WC", detail.camereDoppiaWc)}
                    {field("Doppie no WC", detail.camereDoppiaNoWc)}
                    {field("Retta singola WC", detail.tariffaSingolaWc != null ? `€ ${detail.tariffaSingolaWc}` : null)}
                    {field("Retta singola no WC", detail.tariffaSingolaNoWc != null ? `€ ${detail.tariffaSingolaNoWc}` : null)}
                    {field("Retta doppia WC", detail.tariffaDoppiaWc != null ? `€ ${detail.tariffaDoppiaWc}` : null)}
                    {field("Retta doppia no WC", detail.tariffaDoppiaNoWc != null ? `€ ${detail.tariffaDoppiaNoWc}` : null)}
                  </Section>
                  <Section title="Ospiti accettati e servizi">
                    {field("Autosufficienti", detail.ospitiAutosufficienti ? "Sì" : "No")}
                    {field("Parz. autosufficienti", detail.ospitiParzialmenteAutosufficienti ? "Sì" : "No")}
                    {field("Allettati", detail.ospitiAllettati ? "Sì" : "No")}
                    {field("Alzheimer", detail.ospitiAlzheimer ? "Sì" : "No")}
                    {field("Sedia a rotelle", detail.ospitiSediaRotelle ? "Sì" : "No")}
                    {field("Medico", detail.presenzaMedico)}
                    {field("Infermiere", detail.presenzaInfermiere)}
                    {field("Attività occupazionali", detail.attivitaOccupazionali)}
                    {field("Cucina interna", detail.cucinaInterna ? "Sì" : "No")}
                    {field("Giardino / esterni", detail.giardinoSpaziEsterni)}
                  </Section>
                  <Section title="Disponibilità">
                    {field("Posti totali", detail.postiTotali)}
                    {field("Posti disponibili", detail.postiDisponibili)}
                    {field("Posti diurni", detail.postiDiurni)}
                  </Section>
                  <Section title="Immagini">
                    {(detail.immagini || []).length ? (
                      <div className="flex flex-wrap gap-2">
                        {detail.immagini.map((img) => (
                          <div key={img.id} className="h-16 w-16 rounded border overflow-hidden bg-slate-50">
                            <img
                              src={img.url?.startsWith("http") ? img.url : `${API}${img.url || ""}`}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">Nessuna immagine</p>
                    )}
                  </Section>
                  <Section title="Dati commerciali">
                    {field("P.IVA", detail.partitaIva)}
                    {field("C.F.", detail.codiceFiscale)}
                    {field("PEC", detail.pec)}
                    {field("SDI", detail.sdi)}
                    {field("Società", detail.nomeSocieta)}
                    {field("Data affiliazione", detail.dataAffiliazione)}
                    {field("Ultima chiamata", detail.dataUltimaChiamata)}
                  </Section>
                  <Section title="Relazioni commerciali">
                    {field("Partnership CDRPT", detail.cdrpt_rete?.stato || "Nessuna")}
                    {field("Attivata il", detail.cdrpt_rete?.attivata_at)}
                    {field("Servizi CDRPT attivi", (detail.cdrpt_rete?.servizi_attivi || []).join(", "))}
                    {field("Abbonamento portale CDRPT", detail.cdrpt_abbonamento_portale?.piano || "Nessuno")}
                    {field("Stato abbonamento portale", detail.cdrpt_abbonamento_portale?.stato)}
                    {field("Cliente Affidya", detail.organizationId ? "Sì" : "No")}
                    <p className="md:col-span-2 text-xs text-slate-500">I piani Basic/Premium del portale sono distinti dai piani della suite Affidya.</p>
                  </Section>
                  <Section title="Note e storico">
                    {field("Descrizione", detail.descrizione)}
                    {field("Note call center", detail.noteCallCenter)}
                    {field("Note appuntamenti", detail.noteAppuntamenti)}
                    {field("Note commerciali", detail.noteCommerciali)}
                  </Section>
                </>
              )}

              {showAttivita && (
                <Section title="Storico attività">
                  {!attivita.length ? (
                    <p className="text-sm text-slate-500">Nessuna attività</p>
                  ) : (
                    <ul className="text-sm space-y-1">
                      {attivita.map((a) => (
                        <li key={a.id} className="flex gap-2 text-slate-700">
                          <span className="text-slate-400 whitespace-nowrap">
                            {a.timestamp ? new Date(a.timestamp).toLocaleString("it-IT") : "—"}
                          </span>
                          <span className="font-medium">{a.azione}</span>
                          <span className="text-slate-500">{a.dettaglio}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              )}

              {canDelete && (detail.stats?.users ?? 0) === 0 && (
                <div className="pt-2 border-t flex justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={deletingId === detail.id}
                    onClick={() => handleDelete(detail.id, detail.name)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Elimina struttura
                  </Button>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>
                  Annulla
                </Button>
                <Button onClick={saveEdit} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Salva
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={closeDetail}>
                Chiudi
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
